import * as React from 'react';

import {
  Badge,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@heartplus/ui';
import { ChevronDown } from 'lucide-react';

import {
  groupTokenOptionsByCategory,
  parseMultiSelectTokenValues,
  parseDropdownTokenOptions,
} from './options';
import { INLINE_TOKEN_CLASS, INLINE_TOKEN_DROPDOWN_WIDTH } from './styles';

type EditableMultiSelectTokenProps = {
  value: string;
  label: string;
  optionsValue: string;
  onValueChange: (nextValue: string) => void;
};

interface ToggleValueArgs {
  values: string[];
  nextValue: string;
}

function toggleValue({ values, nextValue }: ToggleValueArgs): string[] {
  return values.includes(nextValue)
    ? values.filter((value) => value !== nextValue)
    : [...values, nextValue];
}

export const EditableMultiSelectToken: React.FC<
  EditableMultiSelectTokenProps
> = ({ value, label, optionsValue, onValueChange }) => {
  const options = React.useMemo(
    () => parseDropdownTokenOptions(optionsValue),
    [optionsValue],
  );
  const groupedOptions = React.useMemo(
    () => groupTokenOptionsByCategory(options),
    [options],
  );
  const selectedValues = React.useMemo(
    () => parseMultiSelectTokenValues(value),
    [value],
  );
  const selectedLabels = React.useMemo(
    () =>
      selectedValues
        .map((selectedValue) => {
          const option = options.find((item) => item.value === selectedValue);
          return option?.label ?? selectedValue;
        })
        .filter((item) => item.trim() !== ''),
    [options, selectedValues],
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          contentEditable={false}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            INLINE_TOKEN_CLASS,
            'justify-between gap-2 border px-2 py-1 text-left',
            INLINE_TOKEN_DROPDOWN_WIDTH,
          )}
        >
          {selectedLabels.length > 0 ? (
            <span className="flex min-w-0 flex-wrap items-center gap-1">
              {selectedLabels.map((selectedLabel) => (
                <Badge
                  key={selectedLabel}
                  variant="outline"
                  className="max-w-full truncate"
                >
                  {selectedLabel}
                </Badge>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">{label || 'Select…'}</span>
          )}
          <ChevronDown className="size-3.5 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[min(28rem,90vw)] max-h-[min(24rem,var(--radix-popover-content-available-height))] overflow-hidden p-2"
      >
        <div
          data-testid="presenting-rhythm-options-scroll"
          className="max-h-full space-y-2 overflow-y-auto overscroll-contain pr-1"
          onWheel={(event) => event.stopPropagation()}
        >
          {groupedOptions.length === 0 ? (
            <div className="px-1 py-1.5 text-sm text-muted-foreground">
              No options available
            </div>
          ) : (
            groupedOptions.map((group) => (
              <details
                key={group.category}
                open
                className="rounded-md border border-border bg-card px-2 py-1"
              >
                <summary className="cursor-pointer select-none text-xs font-medium text-muted-foreground">
                  {group.category}
                </summary>
                <div className="mt-2 space-y-1">
                  {group.options.map((option) => {
                    const checked = selectedValues.includes(option.value);
                    return (
                      <label
                        key={option.value}
                        className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-muted"
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() =>
                            onValueChange(
                              JSON.stringify(
                                toggleValue({
                                  values: selectedValues,
                                  nextValue: option.value,
                                }),
                              ),
                            )
                          }
                          aria-label={option.label}
                        />
                        <span className="min-w-0 flex-1 truncate">
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </details>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
